import { LIST_DEFAULT_LIMIT } from '@pmai/contracts';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

type Order = 'asc' | 'desc';

/**
 * List state (search, filters, sorting, paging) lives in the URL — shareable, survives reload,
 * back button works. Every list page uses this with the shared list contract (`listQuerySchema`).
 */
export function useListParams<S extends string, F extends string = never>(defaults: {
  sort: S;
  order?: Order;
  limit?: number;
  filters?: readonly F[];
}) {
  const [params, setParams] = useSearchParams();
  const limit = defaults.limit ?? LIST_DEFAULT_LIMIT;
  const order: Order =
    params.get('order') === 'desc'
      ? 'desc'
      : params.get('order') === 'asc'
        ? 'asc'
        : (defaults.order ?? 'asc');
  const offset = Math.max(0, Number.parseInt(params.get('offset') ?? '0', 10) || 0);
  const q = params.get('q') ?? '';
  const sort = (params.get('sort') as S | null) ?? defaults.sort;
  const filters = Object.fromEntries((defaults.filters ?? []).map((f) => [f, params.get(f) ?? ''])) as Record<
    F,
    string
  >;

  /** Changes anything but the page → back to the first page. */
  const set = (patch: Partial<Record<'q' | 'sort' | 'order' | 'offset' | F, string | number>>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!('offset' in patch)) next.delete('offset');
        for (const [key, value] of Object.entries(patch)) {
          if (value === '' || value === undefined || (key === 'offset' && value === 0)) next.delete(key);
          else next.set(key, String(value));
        }
        return next;
      },
      { replace: true },
    );

  return { q, sort, order, offset, limit, filters, set };
}

/** Props for a search box that updates the list after typing pauses (no request per keystroke). */
export function useSearchBox(initial: string, apply: (q: string) => void, delayMs = 250) {
  const [value, setValue] = useState(initial);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  return {
    value,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setValue(next);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => apply(next), delayMs);
    },
  };
}
